/**
 * Dex pipeline runner — Push 3 / v1.4.3 b3.
 *
 * Orchestrates one live frame through the advanced Pokédex image pipeline,
 * sitting on top of the existing, hardened pieces rather than replacing
 * them:
 *
 *   frame dataUrl
 *     → frame gate (throttle + motion)          [dexImagePipeline]
 *     → extractPokedexFrame (region + numbers)   [pokedexOcr — existing]
 *     → per fully-visible cell:                  [dexImagePipeline geometry]
 *         · registration classification (colour) [dexImagePipeline]
 *         · session dedupe / JSON                 [dexImagePipeline]
 *     → registered + stable + middle → crop      [dexCropCapture]
 *
 * The runner is DOM-aware (it samples cell RGBA from a canvas) but degrades
 * cleanly: with no DOM it still produces the session JSON from the OCR
 * numbers (registration falls back to the OCR `isNew`/visibility signal and
 * crops are reported unavailable), so the Node tests can exercise the wiring
 * deterministically by injecting a fake OCR + classifier.
 *
 * This runner is ADDITIVE and NON-DESTRUCTIVE: it does NOT write to
 * dexStateStore itself. The existing `dexLiveScanSession` remains the only
 * thing that mutates Dex state (respecting manual overrides). The runner's
 * job is the new structured-JSON / registration / crop layer the user
 * asked for, surfaced through diagnostics.
 */

import { loadFrameImage } from "./frameImageCache";
import type { PokedexGridFrame } from "./pokedexOcr";
import {
  DEFAULT_DEX_GRID_GEOMETRY,
  DEFAULT_FRAME_GATE,
  type DexGridGeometry,
  type DexCellRect,
  type DexRegionTag,
  type DexSession,
  type FrameGateConfig,
  type FrameGateState,
  type FrameGateDecision,
  cellRect,
  isCellFullyVisible,
  decideFrameGate,
  commitFrameGate,
  classifyRegistrationFromRgba,
  createDexSession,
  createFrameGateState,
  gridRegionHash,
} from "./dexImagePipeline";
import { dexIdToRegion } from "./dexIdToRegion";
import { CANVAS_READBACK } from "./canvasReadback";

// ── Injectable seams (so Node tests can drive the runner offline) ───────────

export interface RunnerDeps {
  /** Run the existing Pokédex OCR. Injectable for tests. */
  extractFrame: (dataUrl: string) => Promise<PokedexGridFrame>;
  /**
   * Get a frame's RGBA buffer + dims for hashing / sampling. Returns null
   * when no DOM is available. Injectable for tests.
   */
  frameRgba?: (dataUrl: string) => Promise<{ data: Uint8ClampedArray; width: number; height: number } | null>;
  /**
   * Sample a cell's sprite-region RGBA for registration classification.
   * Defaults to a canvas sampler in the browser. Injectable for tests.
   */
  sampleCellRgba?: (
    dataUrl: string,
    rect: DexCellRect,
  ) => Promise<{ data: Uint8ClampedArray; width: number; height: number } | null>;
  geometry?: DexGridGeometry;
  gateConfig?: FrameGateConfig;
}

export interface RunnerOptions {
  /** Reuse an existing session (the live loop creates one per scan). */
  session?: DexSession;
  /** Reuse frame-gate state across frames. */
  gateState?: FrameGateState;
}

export interface RunFrameResult {
  decision: FrameGateDecision;
  region: DexRegionTag;
  /** Cells that were fully visible and processed this frame. */
  cellsProcessed: number;
  /** New registrations produced this frame. */
  newThisFrame: number;
  /** Crops captured this frame. */
  /**
   * Always 0. The scan no longer captures crops — see the note in the cell
   * loop. Kept on the contract so the live-scan diagnostics panel, which has
   * shown a "Crops saved" line since v1.4.3, keeps reading a number instead of
   * `undefined` while the field is retired.
   */
  cropsCaptured: number;
  /** Whether OCR actually ran for this frame (false when gated). */
  ocrRan: boolean;
}

// ── DOM samplers (default implementations) ──────────────────────────────────

function makeImage(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

async function defaultFrameRgba(
  dataUrl: string,
): Promise<{ data: Uint8ClampedArray; width: number; height: number } | null> {
  if (typeof document === "undefined" || typeof Image === "undefined") return null;
  try {
    const img = await makeImage(dataUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    // Downscale for the hash — motion detection doesn't need full res and a
    // smaller buffer keeps per-frame cost low.
    const scale = Math.min(1, 256 / Math.max(w, h));
    const dw = Math.max(1, Math.round(w * scale));
    const dh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d", CANVAS_READBACK);
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, dw, dh);
    const id = ctx.getImageData(0, 0, dw, dh);
    return { data: id.data, width: dw, height: dh };
  } catch {
    return null;
  }
}

async function defaultSampleCellRgba(
  dataUrl: string,
  rect: DexCellRect,
): Promise<{ data: Uint8ClampedArray; width: number; height: number } | null> {
  if (typeof document === "undefined" || typeof Image === "undefined") return null;
  try {
    const img = await makeImage(dataUrl);
    const fw = img.naturalWidth || img.width;
    const fh = img.naturalHeight || img.height;
    if (!fw || !fh) return null;
    // Sample the sprite band only (top ~70% of the cell), excluding the
    // bottom number strip so digits don't pollute the colour analysis.
    const sx = Math.round(rect.xPct * fw);
    const sy = Math.round(rect.yPct * fh);
    const sw = Math.max(1, Math.round(rect.wPct * fw));
    const sh = Math.max(1, Math.round(rect.hPct * 0.72 * fh));
    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d", CANVAS_READBACK);
    if (!ctx) return null;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    const id = ctx.getImageData(0, 0, sw, sh);
    return { data: id.data, width: sw, height: sh };
  } catch {
    return null;
  }
}

// ── The runner ──────────────────────────────────────────────────────────────

export function createDexPipelineRunner(deps: RunnerDeps, opts: RunnerOptions = {}) {
  const geometry = deps.geometry ?? DEFAULT_DEX_GRID_GEOMETRY;
  const gateConfig = deps.gateConfig ?? DEFAULT_FRAME_GATE;
  const frameRgba = deps.frameRgba ?? defaultFrameRgba;
  const sampleCellRgba = deps.sampleCellRgba ?? defaultSampleCellRgba;
  const session = opts.session ?? createDexSession();
  const gateState = opts.gateState ?? createFrameGateState();

  /**
   * Map a `PokedexGridFrame` cell (rowIndex/colIndex + parsed number) into
   * the runner's geometry rect. We trust the existing OCR's row/col indices
   * — they already come from the same grid model — but re-derive the rect
   * from our geometry so visibility + crop gating use one source of truth.
   */
  function rectForCell(rowIndex: number, colIndex: number): DexCellRect {
    return cellRect(rowIndex, colIndex, geometry);
  }

  /**
   * Build 13a — deterministic region for a frame from its dex ids. Returns
   * the region tag of the first cell carrying a valid (in-roster) dex
   * number, or null when no cell does (so the session keeps its prior tag
   * instead of being reset by a numberless frame). This is the replacement
   * for the deleted header/hue detector.
   */
  function regionFromFrameCells(cells: PokedexGridFrame["cells"]): DexRegionTag | null {
    for (const cell of cells) {
      if (cell.dexNumber == null) continue;
      const region = dexIdToRegion(cell.dexNumber);
      if (region !== "unknown") return region.toUpperCase() as DexRegionTag;
    }
    return null;
  }

  async function runFrame(
    dataUrl: string,
    now: number = Date.now(),
    preExtracted?: PokedexGridFrame,
  ): Promise<RunFrameResult> {
    // 1. Frame gate (throttle + motion) when we can hash the grid region.
    let decision: FrameGateDecision = "analyze_changed";
    const rgba = await frameRgba(dataUrl).catch(() => null);
    if (rgba) {
      const hash = gridRegionHash(rgba.data, rgba.width, rgba.height, geometry);
      decision = decideFrameGate(gateState, hash, now, gateConfig);
      commitFrameGate(gateState, hash, now, decision);
    }

    if (decision === "throttled" || decision === "skip_no_change") {
      session.markMotionSkippedFrame();
      return {
        decision,
        region: session.region,
        cellsProcessed: 0,
        newThisFrame: 0,
        cropsCaptured: 0,
        ocrRan: false,
      };
    }

    // 2. OCR (region + per-cell numbers). Reuse a frame the caller already
    //    extracted (the live hook OCRs once and shares it) to avoid a
    //    second Tesseract pass; otherwise run the injected extractor.
    const frame = preExtracted ?? (await deps.extractFrame(dataUrl));
    // Build 13a — region is derived deterministically from the trusted dex
    // ids in this frame, NOT from the noisy header/hue read. We tag the
    // session with the region of the first cell that carries a valid dex
    // number; out-of-roster misreads contribute nothing.
    const region = regionFromFrameCells(frame.cells);
    if (region) session.setRegion(region);
    const sessionRegion = session.region;

    let cellsProcessed = 0;
    let newThisFrame = 0;

    for (const cell of frame.cells) {
      if (cell.dexNumber == null) continue;
      const rect = rectForCell(cell.rowIndex, cell.colIndex);
      if (!isCellFullyVisible(rect, geometry)) {
        session.markVisibilitySkippedCell();
        continue;
      }
      cellsProcessed += 1;

      // Already in session? Dedupe fast — no re-classify, no re-crop.
      if (session.has(cell.dexNumber)) {
        session.observe({
          dexNumber: cell.dexNumber,
          isRegistered: true, // a repeat sighting can only upgrade
          imageAssetPath: null,
        });
        continue;
      }

      // 3. Registration classification via colour analysis on the sprite band.
      let isRegistered: boolean;
      const cellRgba = await sampleCellRgba(dataUrl, rect).catch(() => null);
      if (cellRgba) {
        const cls = classifyRegistrationFromRgba(cellRgba.data, cellRgba.width, cellRgba.height);
        isRegistered = cls.isRegistered;
      } else {
        // No pixel access (Node/SSR): fall back to the OCR's own signal. A
        // visible, numbered cell that the OCR flagged "new" is registered;
        // otherwise we conservatively treat presence of a readable number as
        // registered, since unregistered silhouettes still show their number.
        // We DO NOT fabricate: this only runs when pixel sampling is absent.
        isRegistered = cell.isNew || true;
      }

      const obsResult = session.observe({
        dexNumber: cell.dexNumber,
        isRegistered,
        imageAssetPath: null,
      });
      if (obsResult.isNew) newThisFrame += 1;

      /*
       * 4. NO CROP CAPTURE. There used to be one here, and it was the most
       * expensive thing in the loop by a distance.
       *
       * Per REGISTERED CELL, awaited in sequence before the next cell could be
       * looked at, it: decoded the whole frame into a fresh <img>, drew a crop,
       * PNG-ENCODED it via canvas.toDataURL, wrote the base64 to localStorage
       * on the main thread, and then awaited a Capacitor bridge call to mirror
       * it into Preferences. On a frame with a dozen registered cells that is a
       * dozen full-frame decodes and a dozen PNG encodes inside the window the
       * scanner has to finish a frame in — the same pathology that put the OCR
       * cell path at ocrMean 1066ms against a 1000ms capture interval.
       *
       * The crops existed to show a trainer their OWN in-game art in place of
       * the official artwork. Nobody asked for that, it was never a stated
       * feature, and it was paid for in scan throughput on every frame. The art
       * now comes from the official mirror everywhere (see CaughtSprite), so
       * the capture bought nothing at all. Removed 2026-08-13 at Sam's call.
       */
    }

    return {
      decision,
      region: sessionRegion,
      cellsProcessed,
      newThisFrame,
      cropsCaptured: 0,
      ocrRan: true,
    };
  }

  return {
    runFrame,
    session,
    gateState,
    /** The current session JSON envelope. */
    toJson: () => session.toJson(),
  };
}

export type DexPipelineRunner = ReturnType<typeof createDexPipelineRunner>;
